import React from "react";
import { Navigate } from "react-router-dom";
import { usePermission } from "../context/PermissionContext";
import { PERMISSION_MODULES } from "common/constants/PermissionConstants";
import AccessRestricted from "Components/Common/AccessRestricted";

interface RouteGuardProps {
  module: string;
  action?: string; 
  children: React.ReactNode;
}

const MODULE_NAME_MAP: Record<string, string> = {
  // Library Module Permissions
  [PERMISSION_MODULES.SPONSOR]: "Library",
  [PERMISSION_MODULES.ALIQUOT]: "Library",
  [PERMISSION_MODULES.PERIOD]: "Library",
  [PERMISSION_MODULES.ROLE]: "Library",
  [PERMISSION_MODULES.USER]: "Library",
  [PERMISSION_MODULES.WORKFLOW]: "Library",
  [PERMISSION_MODULES.CODE_LIST]: "Library",
  [PERMISSION_MODULES.TEMPLATE]: "Library",
  [PERMISSION_MODULES.CRF]: "Library",
  [PERMISSION_MODULES.CHECKLIST]: "Library",
  [PERMISSION_MODULES.DESIGN]: "Library",
  [PERMISSION_MODULES.ADVERSE_EVENT]: "Library",
  [PERMISSION_MODULES.TRAINING_GROUP]: "Library",

  // Pharmacy Module Permissions
  [PERMISSION_MODULES.IP_INVENTORY]: "Pharmacy",
  [PERMISSION_MODULES.DRUG_RETENTION]: "Pharmacy",
  [PERMISSION_MODULES.DISPENSING_LOG]: "Pharmacy",
  [PERMISSION_MODULES.RETURNS_LOG]: "Pharmacy",
  [PERMISSION_MODULES.DRUG_TRANSFER]: "Pharmacy",
  [PERMISSION_MODULES.DRUG_DISPOSAL]: "Pharmacy",
  [PERMISSION_MODULES.STOCK_RECONCILIATION]: "Pharmacy",
  [PERMISSION_MODULES.SUMMARY_REPORTS]: "Pharmacy",

  // Study Module Permissions
  [PERMISSION_MODULES.STUDY]: "Study",
  [PERMISSION_MODULES.DOCUMENT_MANAGEMENT]: "Study",

  // Volunteer Module Permissions
  [PERMISSION_MODULES.VOLUNTEER]: "Volunteer",
  [PERMISSION_MODULES.STUDY_SCREENING]: "Volunteer",
  [PERMISSION_MODULES.DROPOUT_VOLUNTEER]: "Volunteer",
  [PERMISSION_MODULES.BLOCKED_VOLUNTEER]: "Volunteer",
  [PERMISSION_MODULES.TRANSFER_VOLUNTEER]: "Volunteer",

  // Clinical Module Permissions
  [PERMISSION_MODULES.STUDY_DESIGN]: "Clinical",
  [PERMISSION_MODULES.CLINICAL]: "Clinical",
  [PERMISSION_MODULES.SUBJECTS]: "Clinical",
  [PERMISSION_MODULES.CHECKIN_CHECKOUT]: "Clinical",
  [PERMISSION_MODULES.DOSING_SCHEDULE]: "Clinical",
  [PERMISSION_MODULES.PARTICIPANTS]: "Clinical",
  [PERMISSION_MODULES.TIME_POINT]: "Clinical",
  [PERMISSION_MODULES.AMBULATORY_VISIT]: "Clinical",

  // Labels Module Permissions
  [PERMISSION_MODULES.SUBJECT_LABEL]: "Labels",
  [PERMISSION_MODULES.SUBJECT_ICARD_LABEL]: "Labels",
  [PERMISSION_MODULES.VACUTAINER_LABEL]: "Labels",
  [PERMISSION_MODULES.RIAVIAL_LABEL]: "Labels",
  [PERMISSION_MODULES.DISPENSING_LABEL]: "Labels",

  // Session Module Permissions
  [PERMISSION_MODULES.SESSION]: "Session",

  // Audit Trail Module Permissions
  [PERMISSION_MODULES.AUDIT_TRAIL]: "Audit Trail",

  // Dashboard Module Permissions
  [PERMISSION_MODULES.DASHBOARD]: "Dashboard"
};

const RouteGuard: React.FC<RouteGuardProps> = ({ module, action, children }) => {
  const { hasPermission, isModuleVisible } = usePermission();

  // 1. Check if the module is enabled in allowed_modules
  if (module) {
    const modules = module.split(",").map((m) => m.trim());
    const isAnyModuleVisible = modules.some((mod) => {
      const [actualMod] = mod.split(":");
      const topLevelName = MODULE_NAME_MAP[actualMod];
      return topLevelName ? isModuleVisible(topLevelName) : true;
    });

    if (!isAnyModuleVisible) {
      return (
        <div className="page-content">
          <AccessRestricted />
        </div>
      );
    }
  }

  // 2. Check if the user's role has permission
  const isAuthorized = hasPermission(module, action); 

  if (!isAuthorized) {
    if(hasPermission(PERMISSION_MODULES.DASHBOARD)){
      return <Navigate to="/login" replace />;
    }
    else{
      return (
      <>
      <div className="page-content">
        <AccessRestricted />
      </div>
      </>
      );
    }
  }

  return <>{children}</>;
};

export default RouteGuard;
